import { RocheEndpoint, SearchTrackingData, StringService, useTranslation } from '@repo/roche-xx-shared';
import { RocheUISearch, RocheUISearchTabResult } from '@rocheglobal/roche-ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { SearchTrackingType, trackSearchEvent } from '../tracking';
import { useSearchAriaLabels } from '../use-search-aria-labels';

export interface DataSearchProps<TItem> {
  searchTrackingType: SearchTrackingType;
  slot?: string;
  tabName?: string;
  subtabName?: string;
  pageName?: string;
  queryName?: string;
  alwaysShowTabs?: boolean;
  searchTabs?: boolean;
  fetchTabs?: () => Promise<string[]>;
  fetchSubtabs?: () => Promise<string[]>;
  fetchList?: (params: { tab: string; subtab?: string; page: string }) => Promise<RocheEndpoint<TItem[]>>;
  fetchSearch?: (params: { query: string; page: string; tab?: string }) => Promise<RocheEndpoint<Partial<TItem>[]>>;
  itemRenderer: (item: Partial<TItem>, index: number, trackingData: SearchTrackingData) => JSX.Element;
  showInput?: boolean;
}

export const DataSearch = <TItem,>({
  tabName = 'year',
  subtabName = 'tag',
  pageName = 'page',
  queryName = 'query',
  searchTabs = false,
  alwaysShowTabs = false,
  fetchTabs,
  fetchSubtabs,
  fetchList,
  fetchSearch,
  showInput,
  itemRenderer,
  searchTrackingType,
}: DataSearchProps<TItem>): JSX.Element => {
  const { tab = '', subtab = '', query = undefined, page = '1' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const resultsRef = useRef<HTMLDivElement>(null);

  // Track previous values for scroll behavior
  const previousPageRef = useRef(page);
  const previousQueryRef = useRef(query);

  const resultsLabel = StringService.capitalize(useTranslation('results'));
  const searchPlaceholder = StringService.capitalize(useTranslation('enter your search term'));
  const noResultsMessage = StringService.capitalize(useTranslation('no results found'));
  const noResultsTitle = useTranslation('sorry, no results found.');
  const loadingText = useTranslation('loading');

  // aria labels
  const ariaLabels = useSearchAriaLabels();

  const activePageResultsRef = useRef(null);
  const debounceTimer = 800;
  // Track the last tracked search parameters to avoid duplicate tracking
  const lastTrackedSearchRef = useRef<{ query?: string; tab: string; subtab?: string; page: string } | null>(null);

  // Fetch tabs (always when fetchTabs is provided)
  const tabsQuery = useQuery({
    queryKey: ['tabs'],
    queryFn: () => fetchTabs(),
    enabled: !!fetchTabs,
  });

  // Fetch subtabs (only when fetchSubtabs is provided)
  const subtabsQuery = useQuery({
    queryKey: ['subtabs'],
    queryFn: () => fetchSubtabs(),
    enabled: !!fetchSubtabs,
  });

  // Combined loading state for both tab queries
  const areTabsLoading = tabsQuery.isLoading || (!!fetchSubtabs && subtabsQuery.isLoading);

  // Check if subtabs are available
  const hasSubtabs = !!fetchSubtabs && subtabsQuery.data && subtabsQuery.data.length > 0;

  /**
   * Compute the current tab with guards
   * - If tab param is provided, use it
   * - Otherwise, fall back to first available tab from tabsQuery
   * - Memoized to prevent unnecessary recalculations
   */
  const currentTab = useMemo(() => {
    if (tab) return tab;
    if (tabsQuery.data && tabsQuery.data.length > 0) {
      return tabsQuery.data[0];
    }
    return ''; // fallback
  }, [tab, tabsQuery.data]);

  /**
   * Compute the current subtab with guards
   * - If subtab param is provided, match it back to the original value via slug comparison
   * - Otherwise, fall back to first available subtab from subtabsQuery
   * - Memoized to prevent unnecessary recalculations
   */
  const currentSubtab = useMemo(() => {
    if (subtab && subtabsQuery.data) {
      const matched = subtabsQuery.data.find((s) => StringService.urlify(s) === subtab);
      if (matched) return matched;
    }
    if (subtab) return subtab;
    if (subtabsQuery.data && subtabsQuery.data.length > 0) {
      return subtabsQuery.data[0];
    }
    return undefined;
  }, [subtab, subtabsQuery.data]);

  // Don't render if we don't have tabs yet (unless we're searching)
  const hasValidTab = currentTab !== '' || query !== undefined;

  const listQuery = useQuery({
    queryKey: ['list', { page, tab: currentTab, subtab: currentSubtab }],
    queryFn: () => fetchList({ page, tab: currentTab, subtab: currentSubtab }),
    enabled: !!tabsQuery.data && !query,
    placeholderData: keepPreviousData,
  });

  const searchQuery = useQuery({
    queryKey: ['search', { page, query, tab: currentTab }],
    queryFn: () => fetchSearch({ page, query, tab: currentTab }),
    enabled: query !== undefined,
    placeholderData: keepPreviousData,
  });

  // Check if queries have been fetched at least once (to distinguish between initial loading and no results)
  const hasQueriesBeenFetched = listQuery.isFetched || searchQuery.isFetched;

  const prefetch = useCallback(
    ({ page, query }) => {
      if (query) {
        queryClient.prefetchQuery({
          queryKey: ['search', { page, query, tab: currentTab }],
          queryFn: () => fetchSearch({ page, query, tab: currentTab }),
          staleTime: 60000,
        });
      } else {
        queryClient.prefetchQuery({
          queryKey: ['list', { page, tab: currentTab, subtab: currentSubtab }],
          queryFn: () => fetchList({ page, tab: currentTab, subtab: currentSubtab }),
          staleTime: 60000,
        });
      }
    },
    [currentTab, fetchList, fetchSearch, queryClient],
  );

  const buildNavigationPath = (tab: string, subtab?: string, targetPage = '1') => {
    const queryParam = query ? `/${queryName}/${query}` : '';
    const subtabParam = subtab ? `/${subtabName}/${StringService.urlify(subtab)}` : '';
    return `/${tabName}/${tab}${subtabParam}${queryParam}/${pageName}/${targetPage}`;
  };

  const handleTabClick = (newTab: string) => {
    if (currentTab === newTab) return;
    const subtab = hasSubtabs && currentSubtab ? currentSubtab : undefined;
    navigate(buildNavigationPath(newTab, subtab));
  };

  const handleSubtabClick = (newSubtab: string) => {
    if (currentSubtab === newSubtab) return;
    navigate(buildNavigationPath(currentTab, newSubtab));
  };

  const handlePagingClick = (newPage) => {
    const newPageStr = String(newPage);
    if (page === newPageStr) {
      // skip if nothing changed (compare with URL param)
      return;
    }
    const subtab = hasSubtabs && currentSubtab ? currentSubtab : undefined;
    navigate(buildNavigationPath(currentTab, subtab, newPageStr));
  };

  const getCurrentQuery = useCallback(() => {
    if (query) {
      return searchQuery;
    }
    return listQuery;
  }, [query, searchQuery, listQuery]);

  const currentQuery = getCurrentQuery();

  const [searchInputValue, setSearchInputValue] = useState(query || '');

  useEffect(() => {
    if (query) {
      setSearchInputValue(query);
    } else {
      setSearchInputValue('');
    }
  }, [query]);

  useDebounce(
    () => {
      if (searchInputValue && searchInputValue !== query && fetchSearch) {
        let tabParam = '';
        if (searchTabs) {
          tabParam = `/${tabName}/${currentTab}`;
        }
        navigate(`${tabParam}/${queryName}/${searchInputValue}/${pageName}/1`);
      }
      if (searchInputValue === '' && query) {
        navigate(`/${tabName}/${currentTab}/${pageName}/1`);
      }
    },
    debounceTimer,
    [searchInputValue, currentTab],
  );

  const trackSearch = useCallback(() => {
    const currentSearchParams = {
      query: query || undefined,
      tab: currentTab,
      page: page,
    };

    // Check if we've already tracked this exact search
    const lastTracked = lastTrackedSearchRef.current;
    if (
      lastTracked &&
      lastTracked.query === currentSearchParams.query &&
      lastTracked.tab === currentSearchParams.tab &&
      lastTracked.page === currentSearchParams.page &&
      lastTracked.subtab === currentSubtab
    ) {
      return; // Already tracked this event
    }

    trackSearchEvent(
      {
        term: searchInputValue,
        results: activePageResultsRef.current,
        filter: {
          pagination: parseInt(page, 10),
          filter: currentSubtab ? `${currentTab} | ${currentSubtab}` : currentTab,
        },
      },
      searchTrackingType,
    );

    // Update the last tracked search
    lastTrackedSearchRef.current = currentSearchParams;
  }, [query, currentTab, currentSubtab, page, searchInputValue, searchTrackingType]);

  if (currentQuery.data?.Items.length) {
    activePageResultsRef.current = currentQuery.data.Total;
  }

  useEffect(() => {
    if (currentQuery.isSuccess) {
      trackSearch();
    }
  }, [currentQuery.isSuccess, trackSearch]);

  // scroll behavior implementation
  useEffect(() => {
    const pageChanged = previousPageRef.current !== page;
    const queryChanged = previousQueryRef.current !== query;

    // Only scroll when page changes
    if (pageChanged && !queryChanged) {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    previousPageRef.current = page;
    previousQueryRef.current = query;
  }, [page, query]);

  const resultsInTabs: RocheUISearchTabResult<string, Partial<TItem>>[] =
    tabsQuery?.data?.map((tab) => {
      return {
        tab: tab,
        variant: 'pages',
        title: tab,
        items: tab == currentTab ? (currentQuery?.data?.Items ?? []) : [],
        totalItems: tab == currentTab ? (currentQuery?.data?.Total ?? 0) : 0,
      };
    }) ?? [];
  const currentResults = resultsInTabs.find((resultWithTab) => resultWithTab.tab === currentTab);
  const currentPage = parseInt(page);

  // Simply show a loading state if we don't have a valid tab yet
  if (!hasValidTab && tabsQuery.isLoading) {
    return (
      <RocheUISearch
        variant='data'
        itemRenderer={() => <></>}
        showInput={showInput}
        searchResults={[]}
        resultsRef={resultsRef}
        searchState={{
          activeTab: '',
          activeSubtab: undefined,
          page: 1,
          query: '',
        }}
        labels={{
          searchPlaceholder,
          totalResultsFoundText: '',
          loadingText,
          noResultsMessage,
          noResultsTitle,
        }}
        onSearchParamsChange={() => {}}
        isLoading={true}
        hasQueriesBeenFetched={false}
        ariaLabels={ariaLabels}
      />
    );
  }

  return (
    <RocheUISearch
      variant='data'
      alwaysShowTabs={alwaysShowTabs}
      itemRenderer={(item, _tab, index, _page) =>
        itemRenderer(item, index, {
          term: searchInputValue,
          results: currentResults?.totalItems ?? 0,
          resultPosition: index,
          filter: {
            pagination: currentPage,
            filter: currentSubtab ? `${currentTab} | ${currentSubtab}` : currentTab,
          },
          searchComponent: searchTrackingType,
        })
      }
      showInput={showInput}
      searchResults={resultsInTabs}
      resultsRef={resultsRef}
      subtabs={hasSubtabs ? subtabsQuery.data : undefined}
      searchState={{
        activeTab: currentTab,
        activeSubtab: currentSubtab,
        page: currentPage,
        query: searchInputValue,
      }}
      labels={{
        searchTitle: 'Search',
        searchPlaceholder,
        totalResultsFoundText:
          currentQuery.data?.Items.length > 0 ? `${currentQuery.data.Total} ${resultsLabel}` : noResultsTitle,
        loadingText,
        noResultsMessage,
      }}
      onSearchParamsChange={(searchParams) => {
        setSearchInputValue(searchParams.query);
        handlePagingClick(searchParams.page);
        handleTabClick(searchParams.activeTab);
        if (searchParams.activeSubtab) {
          handleSubtabClick(searchParams.activeSubtab);
        }
      }}
      isLoading={areTabsLoading || currentQuery.isFetching}
      hasQueriesBeenFetched={hasQueriesBeenFetched}
      onPagePrefetch={(_, page) => {
        if (page > 0) {
          prefetch({ page, query });
        }
      }}
      ariaLabels={ariaLabels}
    />
  );
};
